import { useMemo, useState } from 'react'
import { logout, setEnrollment, setAcademicYear } from '../store/session'
import { getAcademicYears } from '../store/lookup'
import { useSelector, useDispatch } from 'react-redux'
import {
  Avatar, Box, Chip, Dialog, DialogContent, DialogTitle,
  Divider, Grid, IconButton, LinearProgress,
  ListItemIcon, ListItemText, Menu, MenuItem,
  Stack, ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material'
import { Link } from 'react-router-dom'
import { CheckCircleOutlineIcon, FiBell } from '../components/Icons'
import { useSession } from '../components/Providers'
import { useTranslation } from 'react-i18next'
import { appearance } from 'store/session'
import {
  AccountCircleOutlined as ProfileIcon,
  CalendarMonthOutlined as CalendarIcon,
  CheckCircle as CheckCircleIcon,
  DarkMode,
  LightMode,
  LogoutOutlined as LogoutIcon,
  SettingsBrightness,
  SwitchAccountOutlined as SwitchAccountIcon,
} from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { capitalize } from 'lodash'
import { stringToColor } from 'components/Avatar'

const languages = {
  en: { nativeName: 'English' },
  es: { nativeName: 'Spanish' },
}

function AcademicYearDialog({ open, onClose }) {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { academicYear } = useSession()
  const academicYears = useSelector(state => state.lookup.academicYears)

  const handleSelect = year => {
    if (academicYear?.id !== year.id) dispatch(setAcademicYear({ id: year.id }))
    onClose()
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <CalendarIcon color="primary" />
          <Typography variant="h6" fontWeight={700}>{t('Switch Academic Year')}</Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {t('Select an academic year to switch to')}
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ pt: 0.5 }}>
        {!academicYears ? (
          <LinearProgress />
        ) : (
          <Stack spacing={1.5} sx={{ pb: 1 }}>
            {academicYears.map(year => {
              const isActive = year.id === academicYear?.id
              return (
                <Box
                  key={year.id}
                  onClick={() => handleSelect(year)}
                  sx={{
                    p: 1.75,
                    border: '1px solid',
                    borderColor: isActive ? 'primary.main' : 'divider',
                    borderLeft: '4px solid',
                    borderLeftColor: isActive ? 'primary.main' : 'divider',
                    borderRadius: 2,
                    cursor: 'pointer',
                    bgcolor: isActive ? 'primary.50' : 'background.paper',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    transition: 'all 0.15s',
                    '&:hover': { borderColor: 'primary.main', borderLeftColor: 'primary.main', bgcolor: 'primary.50' },
                  }}
                >
                  <Typography
                    variant="body2"
                    fontWeight={isActive ? 700 : 500}
                    color={isActive ? 'primary.main' : 'text.primary'}
                  >
                    {year.name}
                  </Typography>
                  {isActive && <CheckCircleIcon color="primary" fontSize="small" />}
                </Box>
              )
            })}
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  )
}

export default function UserMenu() {
  const dispatch = useDispatch()
  const { t, i18n } = useTranslation()
  const [anchorElUser, setAnchorElUser] = useState(null)
  const [yearDialogOpen, setYearDialogOpen] = useState(false)
  const mode = useSelector(state => state.session.appearance)
  const navigate = useNavigate()
  const { user, district, campus, school, person, academicYear } = useSession()
  const enrollments = useSelector(state => state.session.enrollments)
  const academicYears = useSelector(state => state.lookup.academicYears)

  const showAcademicYearSwitch = school?.id && person?.type === 'staff' && district?.isServiceProvider === false

  const handleOpenUserMenu = event => setAnchorElUser(event.currentTarget)
  const handleCloseUserMenu = () => setAnchorElUser(null)
  const handleModeChange = (_, m) => dispatch(appearance(m))
  const handleLangChange = (_, lang) => i18n.changeLanguage(lang)

  const handleOpenYearDialog = () => {
    handleCloseUserMenu()
    if (!academicYears) dispatch(getAcademicYears())
    setYearDialogOpen(true)
  }

  const locationLabel = campus?.name || school?.name || district?.name || ''
  const initials = person
    ? `${person.firstName?.[0] ?? ''}${person.lastName?.[0] ?? ''}`.toUpperCase()
    : '?'
  const avatarBg = stringToColor(campus?.abbreviation)

  const menuItemList = () => {
    if (!enrollments) return <Box sx={{ width: '100%' }}><LinearProgress /></Box>
    const unique = [...new Map(enrollments.map(ms => [`${ms.campusId || ms.districtId}-${ms.personId}`, ms])).values()]
    return unique.map(ms => {
      const isSelected = ms.campusId
        ? ms.campusId === campus?.id && ms.personId === person.id
        : ms.districtId === district?.id && ms.personId === person.id
      return (
        <MenuItem
          key={`ms-${ms?.campusId || ms?.districtId}-${ms?.id}`}
          selected={isSelected}
          onClick={() => {
            handleCloseUserMenu()
            navigate('/')
            dispatch(setEnrollment({ id: ms?.id, ...(ms.campusId ? {} : { campus: 'null' }) }))
          }}
        >
          <ListItemIcon>
            <SwitchAccountIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary={`${capitalize(ms.type)} @ ${ms.campusName || ms.districtName}`}
            secondary={ms.personName}
            primaryTypographyProps={{ variant: 'body2', fontWeight: isSelected ? 700 : 400 }}
            secondaryTypographyProps={{ variant: 'caption' }}
          />
          {isSelected && <CheckCircleOutlineIcon fontSize="small" color="primary" sx={{ ml: 1 }} />}
        </MenuItem>
      )
    })
  }

  const menuItems = useMemo(() => menuItemList(), [enrollments, campus, district, person])

  return (
    <Grid container alignItems="center" justifyContent="center" sx={{ ml: 0, pl: -2 }}>
      <FiBell size="24px" />
      <Grid item sx={{ flexGrow: 0, ml: 2 }}>
        <Tooltip title={t('Account settings')}>
          <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
            <Avatar
              alt={user?.firstName}
              src={user?.avatar?.location}
              sx={{ width: 40, height: 40, bgcolor: avatarBg, fontWeight: 700 }}
            >
              {!user?.avatar?.location && initials}
            </Avatar>
          </IconButton>
        </Tooltip>

        <Menu
          sx={{ mt: '45px' }}
          anchorEl={anchorElUser}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          open={Boolean(anchorElUser)}
          onClose={handleCloseUserMenu}
          PaperProps={{ sx: { minWidth: 280 } }}
        >
          <Box sx={{ px: 2, py: 1.5 }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Avatar
                src={user?.avatar?.location}
                sx={{ width: 44, height: 44, bgcolor: avatarBg, fontWeight: 700, fontSize: 15 }}
              >
                {!user?.avatar?.location && initials}
              </Avatar>
              <Box>
                <Typography variant="subtitle2" fontWeight={700} lineHeight={1.2}>
                  {person?.firstName} {person?.lastName}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  {user?.email}
                </Typography>
                {locationLabel && (
                  <Chip label={locationLabel} size="small" sx={{ mt: 0.5, height: 18, fontSize: 11 }} />
                )}
              </Box>
            </Stack>
          </Box>

          <Divider />

          <MenuItem component={Link} to="/profile" onClick={handleCloseUserMenu}>
            <ListItemIcon><ProfileIcon fontSize="small" /></ListItemIcon>
            <ListItemText>{t('Profile')}</ListItemText>
          </MenuItem>

          {showAcademicYearSwitch && (
            <MenuItem onClick={handleOpenYearDialog}>
              <ListItemIcon><CalendarIcon fontSize="small" /></ListItemIcon>
              <ListItemText
                primary={t('Switch Academic Year')}
                secondary={academicYear?.name}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </MenuItem>
          )}

          <Divider />

          <Box sx={{ px: 2, pt: 1, pb: 0.5 }}>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.75 }}>
              {t('Appearance')}
            </Typography>
            <ToggleButtonGroup value={mode} exclusive onChange={handleModeChange} size="small" fullWidth>
              <ToggleButton value=""><SettingsBrightness fontSize="small" /><Typography variant="caption" sx={{ ml: 0.5 }}>Auto</Typography></ToggleButton>
              <ToggleButton value="light"><LightMode fontSize="small" /><Typography variant="caption" sx={{ ml: 0.5 }}>Light</Typography></ToggleButton>
              <ToggleButton value="dark"><DarkMode fontSize="small" /><Typography variant="caption" sx={{ ml: 0.5 }}>Dark</Typography></ToggleButton>
            </ToggleButtonGroup>
          </Box>

          <Box sx={{ px: 2, pt: 1, pb: 1 }}>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.75 }}>
              {t('Language')}
            </Typography>
            <ToggleButtonGroup value={i18n.resolvedLanguage} exclusive onChange={handleLangChange} size="small" fullWidth>
              {Object.entries(languages).map(([lang, { nativeName }]) => (
                <ToggleButton key={lang} value={lang}>
                  <Typography variant="caption">{nativeName}</Typography>
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>

          {person?.type !== 'parent' && (
            <>
              <Divider />
              <Box sx={{ px: 2, pt: 1 }}>
                <Typography variant="caption" color="text.secondary">{t('Switch Account')}</Typography>
              </Box>
              {menuItems}
            </>
          )}

          <Divider />

          <MenuItem onClick={() => dispatch(logout())} sx={{ color: 'error.main' }}>
            <ListItemIcon><LogoutIcon fontSize="small" sx={{ color: 'error.main' }} /></ListItemIcon>
            <ListItemText>{t('Logout')}</ListItemText>
          </MenuItem>
        </Menu>
      </Grid>

      <AcademicYearDialog
        open={yearDialogOpen}
        onClose={() => setYearDialogOpen(false)}
      />
    </Grid>
  )
}
